import { useRef } from "react";
import { motion } from "framer-motion";
import { Music2, Wind, Drum, Piano, Guitar, Calendar, MapPin, Users, Mail, ChevronRight } from "lucide-react";
import RegistrationForm from "@/components/RegistrationForm";

const ASSET = "/assets";

const families = [
  { key: "Archi", icon: Music2, desc: "Violino · Viola · Violoncello", img: "https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=900&q=80" },
  { key: "Fiati", icon: Wind, desc: "Flauto · Oboe · Clarinetto · Fagotto · Corno · Sax · Tromba", img: "https://images.unsplash.com/photo-1581283869631-baae04233b04?w=900&q=80" },
  { key: "Percussioni", icon: Drum, desc: "Batteria · Timpani · Xilofono · Metallofono", img: "https://images.unsplash.com/photo-1588032786045-59cefda005c0?w=900&q=80" },
  { key: "Tastiere e Pianoforte", icon: Piano, desc: "Pianoforte · Tastiere", img: "https://images.pexels.com/photos/34767574/pexels-photo-34767574.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { key: "Chitarre e Corde", icon: Guitar, desc: "Classica · Acustica · Elettrica · Basso", img: "https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=900&q=80" },
];

const fade = { hidden: { opacity: 0, y: 28 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }) };

export default function Landing() {
  const formRef = useRef(null);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-ink text-white overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#1a1416]/70 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="nav-brand">
            <img src={`${ASSET}/logo.png`} alt="Trinity Musical School" className="h-9 w-9 object-contain rounded" />
            <span className="font-cond uppercase tracking-wide text-sm sm:text-base text-white/90">Trinity Young Orchestra</span>
          </div>
          <button onClick={scrollToForm} data-testid="nav-cta" className="bg-bordeaux hover:bg-[#c8164b] transition-colors text-white text-sm font-bold px-5 py-2 rounded-full">
            Iscriviti
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-28 pb-16 px-5 grain">
        <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_15%_0%,#A8123E_0%,#4f0a1f_45%,#1a1416_100%)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div>
            <motion.p variants={fade} initial="hidden" animate="show" className="font-cond uppercase tracking-[0.25em] text-white/70 text-sm mb-4">
              Trinity Musical School - Ferrara | Rovigo
            </motion.p>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} className="font-display uppercase leading-none whitespace-nowrap text-[8vw] sm:text-[7vw] lg:text-7xl">
              TRINITY YOUNG ORCHESTRA
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="mt-6 text-white/80 text-lg max-w-xl mx-auto">
              Entra a far parte dell'orchestra giovanile e suona insieme ad altri ragazzi appassionati e preparati.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-7 flex flex-wrap gap-3 justify-center">
              <Chip icon={MapPin} text="Provincia di Ferrara" />
              <Chip icon={MapPin} text="Provincia di Rovigo" />
              <Chip icon={Users} text="10 – 20 anni" />
              <Chip icon={Music2} text="Se sai suonare, invia la tua candidatura" />
            </motion.div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-8 flex flex-wrap gap-3 justify-center">
              <button onClick={scrollToForm} data-testid="hero-cta" className="group bg-white text-[#7a0e2e] font-extrabold px-7 py-3.5 rounded-full flex items-center gap-2 hover:bg-cream transition-colors">
                Candidati ora <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAMILIES */}
      <section className="py-20 px-5 bg-ink">
        <div className="max-w-6xl mx-auto">
          <SectionTitle kicker="Le sezioni" title="Selezioni per ogni strumento" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {families.map((f, i) => (
              <motion.div key={f.key} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="group relative rounded-2xl overflow-hidden border border-white/10 min-h-[230px] flex items-end" data-testid={`family-card-${i}`}>
                <img src={f.img} alt={f.key} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4f0a1f] via-[#4f0a1f]/40 to-transparent" />
                <div className="relative p-5">
                  <f.icon className="text-white mb-2" size={26} />
                  <h3 className="font-display uppercase text-2xl leading-none">{f.key}</h3>
                  <p className="text-white/75 text-sm mt-1.5">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="py-20 px-5 bg-bordeaux grain relative">
        <div className="max-w-5xl mx-auto relative">
          <SectionTitle kicker="Chi può candidarsi" title="A chi è rivolto il progetto" light />
          <p className="text-white/90 text-xl max-w-3xl mt-6">
            Cerchiamo ragazzi e ragazze <strong>dai 10 ai 20 anni</strong> che stanno studiando uno strumento. Il Progetto intende essere uno sbocco musicale per ragazzi che frequentano:
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[
              "Scuole di musica private",
              "Scuole secondarie ad indirizzo musicale",
              "Corsi di base o propedeutici dei conservatori",
            ].map((t, i) => (
              <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-5 backdrop-blur-sm" data-testid={`eligibility-${i}`}>
                <span className="font-display text-3xl text-white/40">0{i + 1}</span>
                <p className="mt-2 font-semibold">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 px-5 bg-ink">
        <div className="max-w-5xl mx-auto">
          <SectionTitle kicker="Come funziona" title="Tre passi verso il palco" />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            <Step n="1" icon={Mail} title="Compila il form" text="Invia la tua candidatura online in pochi minuti." />
            <Step n="2" icon={Calendar} title="Convocazione" text="I candidati saranno convocati via email con data e luogo della prova conoscitiva." />
            <Step n="3" icon={Music2} title="Selezione" text="Le selezioni si terranno periodicamente, i candidati saranno contattati per la prova conoscitiva. Il progetto opera in provincia di Ferrara e di Rovigo." />
          </div>
        </div>
      </section>

      {/* FORM */}
      <section ref={formRef} className="py-20 px-5 bg-gradient-to-b from-[#1a1416] to-[#2a1a20]" data-testid="form-section">
        <div className="max-w-3xl mx-auto">
          <SectionTitle kicker="Candidatura" title="Candidati alle selezioni" />
          <p className="text-white/70 mt-4 mb-8">Compila il modulo: riceverai la convocazione via email. Tutte le info su <a href="https://www.trinityoungorchestra.eu" target="_blank" rel="noreferrer" className="text-bordeaux font-semibold underline">www.trinityoungorchestra.eu</a>.</p>
          <RegistrationForm families={families.map((f) => f.key)} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#4f0a1f] px-5 py-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <img src={`${ASSET}/logo.png`} alt="logo" className="h-12 w-12 object-contain rounded" />
            <div>
              <p className="font-cond uppercase tracking-wide">Trinity Musical School</p>
              <p className="text-white/60 text-sm">Amici per la Musica APS · Ferrara | Rovigo</p>
            </div>
          </div>
          <div className="text-white/70 text-sm text-center">
            <p className="font-cond uppercase tracking-wide text-white/50 text-xs mb-1">Direzione</p>
            <p className="whitespace-nowrap"><span className="text-white/90 font-semibold">Presidente:</span> Lucia Porri</p>
            <p className="whitespace-nowrap"><span className="text-white/90 font-semibold">Direttore Artistico:</span> Stefano Ongaro</p>
            <p className="whitespace-nowrap"><span className="text-white/90 font-semibold">Direttore Musicale:</span> José Angel Ramirez Rogoitia</p>
          </div>
          <div className="text-white/70 text-sm">
            <p>amministrazione@amiciperlamusica.org</p>
            <a href="https://www.trinityoungorchestra.eu" target="_blank" rel="noreferrer" className="text-white font-semibold underline" data-testid="footer-website">www.trinityoungorchestra.eu</a>
            <p className="text-white/40 mt-1">Amici per la Musica · amiciperlamusica.org</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Chip({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-medium backdrop-blur-sm">
      <Icon size={16} /> {text}
    </span>
  );
}

function SectionTitle({ kicker, title, light }) {
  return (
    <div>
      <p className={`font-cond uppercase tracking-[0.25em] text-sm ${light ? "text-white/70" : "text-bordeaux"}`}>{kicker}</p>
      <h2 className="font-display uppercase text-4xl sm:text-5xl mt-2 leading-none">{title}</h2>
    </div>
  );
}

function Step({ n, icon: Icon, title, text }) {
  return (
    <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative bg-white/5 border border-white/10 rounded-2xl p-7">
      <span className="absolute -top-4 -left-2 font-display text-6xl text-bordeaux/40">{n}</span>
      <Icon className="text-bordeaux mb-3 relative" size={30} />
      <h3 className="font-bold text-xl">{title}</h3>
      <p className="text-white/70 mt-2 text-sm">{text}</p>
    </motion.div>
  );
}
